import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { format, startOfMonth, endOfMonth, isWithinInterval } from 'date-fns';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useInvoice } from '../context/InvoiceContext';
import { generatePDF } from '../utils/pdfGenerator';
import { generateTravelPDF } from '../utils/travelPdfGenerator';
import { generateMarketingPDF } from '../utils/marketingPdfGenerator';
import { generateExcursionPDF } from '../utils/excursionPdfGenerator';

const { FiEye, FiDownload, FiTrash2, FiEdit, FiSearch, FiCalendar, FiDollarSign, FiGlobe, FiBriefcase, FiCompass, FiMail, FiLoader, FiRepeat, FiRefreshCw } = FiIcons;

const InvoiceList = () => {
  const { invoices, deleteInvoice, settings, updateInvoiceStatus } = useInvoice();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [filterType, setFilterType] = useState('all');
  const [sendingEmailId, setSendingEmailId] = useState(null);
  const [checkingStatusId, setCheckingStatusId] = useState(null);

  const currencies = {
    USD: { symbol: '$', name: 'US Dollar' },
    EUR: { symbol: '€', name: 'Euro' },
    BRL: { symbol: 'R$', name: 'Brazilian Real' },
    GBP: { symbol: '£', name: 'British Pound' }
  };

  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = invoice.customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.number.toString().includes(searchTerm);
    
    const type = invoice.invoiceType || 'default';
    const matchesType = filterType === 'all' || type === filterType;
    
    return matchesSearch && matchesType;
  });

  const sortedInvoices = [...filteredInvoices].sort((a, b) => {
    switch (sortBy) {
      case 'date':
        return new Date(b.createdAt) - new Date(a.createdAt);
      case 'number':
        return String(b.number).localeCompare(String(a.number));
      case 'customer':
        return a.customer.name.localeCompare(b.customer.name);
      case 'total':
        return b.total - a.total;
      default:
        return 0;
    }
  });

  const currentMonthInvoices = useMemo(() => {
    const now = new Date();
    const monthStart = startOfMonth(now);
    const monthEnd = endOfMonth(now);
    
    return invoices.filter(invoice => {
      const invoiceDate = new Date(invoice.createdAt);
      return isWithinInterval(invoiceDate, { start: monthStart, end: monthEnd });
    });
  }, [invoices]);

  const monthlyRevenueByCurrency = useMemo(() => {
    const revenueByCurrency = {};
    currentMonthInvoices.forEach(invoice => {
      const currency = invoice.currency || invoice.pricing?.currency || 'USD';
      if (!revenueByCurrency[currency]) {
        revenueByCurrency[currency] = 0;
      }
      revenueByCurrency[currency] += invoice.total;
    });
    return revenueByCurrency;
  }, [currentMonthInvoices]);

  const monthlyAverageInvoice = useMemo(() => {
    if (currentMonthInvoices.length === 0) return 0;
    const totalMonthlyRevenue = currentMonthInvoices.reduce((sum, inv) => sum + inv.total, 0);
    return totalMonthlyRevenue / currentMonthInvoices.length;
  }, [currentMonthInvoices]);

  const handleDownload = async (invoice) => {
    try {
      if (invoice.invoiceType === 'travel') {
        await generateTravelPDF(invoice);
      } else if (invoice.invoiceType === 'marketing') {
        await generateMarketingPDF(invoice);
      } else if (invoice.invoiceType === 'excursion') {
        await generateExcursionPDF(invoice);
      } else {
        await generatePDF(invoice);
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this invoice?')) {
      deleteInvoice(id);
    }
  };

  const handleSendEmail = async (invoice) => {
    if (!invoice || !invoice.id) {
      alert("Cannot send email: Invoice data is incomplete (missing ID).");
      return;
    }
    if (!invoice.customer.email) {
      alert("This customer does not have an email address.");
      return;
    }

    setSendingEmailId(invoice.id);

    const origin = import.meta.env.VITE_APP_URL || (window.location.protocol + "//" + window.location.host);
    const paymentLink = `${origin}/#/public/invoice/${invoice.id}`;

    if (!origin || origin.includes('undefined')) {
        alert("Cannot send email: Application origin URL could not be determined.");
        setSendingEmailId(null);
        return;
    }

    const payload = {
      action: 'sendInvoiceEmail',
      payload: {
        paymentLink: paymentLink,
        customerName: invoice.customer.name,
        customerEmail: invoice.customer.email,
        invoiceNumber: invoice.fullNumber,
        total: invoice.total,
        currency: invoice.currency || invoice.pricing?.currency,
        status: invoice.status,
        companyName: settings.companyInfo?.name || 'Your Company',
        companyEmail: settings.companyInfo?.email,
        companyLogo: settings.logoUrl,
      }
    };

    try {
      const response = await fetch('/api/google-script-proxy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const result = await response.json();
      if (result.status === 'success') {
        alert('Email sent successfully!');
      } else {
        throw new Error(result.message || 'Failed to send email.');
      }
    } catch (error) {
      console.error("Error sending email:", error);
      alert(`Error sending email: ${error.message}`);
    } finally {
      setSendingEmailId(null);
    }
  };

  const handleCheckStatus = async (invoice) => {
    if (!invoice.paymentIntentId && !invoice.checkoutSessionId) {
      alert('No payment attempt recorded for this invoice.');
      return;
    }
    setCheckingStatusId(invoice.id);
    try {
      const params = new URLSearchParams({ invoiceId: invoice.id });
      if (invoice.checkoutSessionId) {
        params.append('checkoutSessionId', invoice.checkoutSessionId);
      } else if (invoice.paymentIntentId) {
        params.append('paymentIntentId', invoice.paymentIntentId);
      }

      const response = await fetch(`/api/check-payment-status?${params.toString()}`);
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to check status.');
      }

      if (result.status) {
        updateInvoiceStatus(invoice.id, result.status);
        alert(`Status for invoice #${invoice.number} is: ${result.status.toUpperCase()}`);
      }

    } catch (error) {
      console.error('Error checking payment status:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setCheckingStatusId(null);
    }
  };

  const formatPaymentDate = (invoice) => {
    const type = invoice.invoiceType || 'default';
    if (type === 'travel') {
      if (invoice.returnDate) {
        return `${format(new Date(invoice.travelDate), 'MMM dd, yyyy')} - ${format(new Date(invoice.returnDate), 'MMM dd, yyyy')}`;
      }
      return format(new Date(invoice.travelDate), 'MMM dd, yyyy');
    }
    
    if (type === 'excursion') {
      return format(new Date(invoice.excursionDetails?.excursionDate || Date.now()), 'MMM dd, yyyy');
    }
    
    if (invoice.isDateRange && invoice.paymentEndDate) {
      return `${format(new Date(invoice.paymentDate), 'MMM dd, yyyy')} - ${format(new Date(invoice.paymentEndDate), 'MMM dd, yyyy')}`;
    }
    return format(new Date(invoice.paymentDate), 'MMM dd, yyyy');
  };

  const getCurrencySymbol = (code) => currencies[code]?.symbol || '$';

  const getEditPath = (invoice) => {
    const type = invoice.invoiceType || 'default';
    switch (type) {
      case 'travel':
        return `/travel-edit/${invoice.id}`;
      case 'marketing':
        return `/marketing-edit/${invoice.id}`;
      case 'excursion':
        return `/excursion-edit/${invoice.id}`;
      default:
        return `/edit/${invoice.id}`;
    }
  };

  const currentMonthName = format(new Date(), 'MMMM yyyy');

  const renderInvoiceType = (invoice) => {
    const type = invoice.invoiceType || 'default';
    switch (type) {
      case 'travel':
        return (
          <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-cyan-100 text-cyan-800">
            <SafeIcon icon={FiGlobe} className="w-3 h-3 mr-1" />
            Travel
          </span>
        );
      case 'marketing':
        return (
          <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
            <SafeIcon icon={FiBriefcase} className="w-3 h-3 mr-1" />
            Marketing
          </span>
        );
      case 'excursion':
        return (
          <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">
            <SafeIcon icon={FiCompass} className="w-3 h-3 mr-1" />
            Excursion
          </span>
        );
      default:
        return (
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            invoice.isExportation 
              ? 'bg-green-100 text-green-800' 
              : 'bg-blue-100 text-blue-800'
          }`}>
            {invoice.isExportation ? 'Export' : 'Standard'}
          </span>
        );
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Invoices & Bookings</h1>
            <p className="text-gray-600 mt-1">Manage and track all your invoices and bookings</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search invoices..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Types</option>
              <option value="default">Standard</option>
              <option value="marketing">Marketing</option>
              <option value="travel">Travel</option>
              <option value="excursion">Excursion</option>
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="date">Sort by Date</option>
              <option value="number">Sort by Number</option>
              <option value="customer">Sort by Customer</option>
              <option value="total">Sort by Total</option>
            </select>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Monthly Overview - {currentMonthName}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <motion.div className="bg-white p-6 rounded-xl shadow-lg" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-lg"><SafeIcon icon={FiCalendar} className="w-6 h-6 text-blue-600" /></div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Monthly Invoices</p>
                <p className="text-2xl font-bold text-gray-900">{currentMonthInvoices.length}</p>
                <p className="text-xs text-gray-500">Total: {invoices.length}</p>
              </div>
            </div>
          </motion.div>
          <motion.div className="bg-white p-6 rounded-xl shadow-lg" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <div className="flex items-start">
              <div className="p-3 bg-green-100 rounded-lg"><SafeIcon icon={FiDollarSign} className="w-6 h-6 text-green-600" /></div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-600">Monthly Revenue</p>
                {Object.keys(monthlyRevenueByCurrency).length > 0 ? (
                  <div className="space-y-1 mt-1">
                    {Object.entries(monthlyRevenueByCurrency).map(([currency, amount]) => (
                      <div key={currency} className="flex justify-between items-center">
                        <span className="text-2xl font-bold text-gray-900">{getCurrencySymbol(currency)}{amount.toFixed(2)}</span>
                        <span className="text-xs font-medium bg-gray-100 px-2 py-1 rounded-full text-gray-600">{currency}</span>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-2xl font-bold text-gray-900">$0.00</p>}
              </div>
            </div>
          </motion.div>
          <motion.div className="bg-white p-6 rounded-xl shadow-lg" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <div className="flex items-center">
              <div className="p-3 bg-purple-100 rounded-lg"><SafeIcon icon={FiEye} className="w-6 h-6 text-purple-600" /></div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Monthly Avg. Invoice</p>
                <p className="text-2xl font-bold text-gray-900">${monthlyAverageInvoice.toFixed(2)}</p>
                <p className="text-xs text-gray-500">Based on {currentMonthInvoices.length} invoices this month</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {sortedInvoices.length === 0 ? (
        <motion.div className="text-center py-12" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiCalendar} className="w-12 h-12 text-gray-400" />
          </div>
          <h3 className="text-xl font-medium text-gray-900 mb-2">No invoices found</h3>
          <p className="text-gray-600">{searchTerm ? 'Try adjusting your search criteria' : 'Create your first invoice to get started'}</p>
        </motion.div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedInvoices.map((invoice, index) => (
                  <motion.tr key={invoice.id} className="hover:bg-gray-50" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="flex items-center">
                          <div className="text-sm font-medium text-gray-900">{invoice.fullNumber || `#${invoice.number}`}</div>
                          {invoice.isRecurring && (
                            <SafeIcon icon={FiRepeat} className="w-4 h-4 ml-2 text-blue-500" title="Recurring Invoice" />
                          )}
                        </div>
                        <div className="text-sm text-gray-500">{format(new Date(invoice.createdAt), 'MMM dd, yyyy')}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{invoice.customer.name}</div>
                        <div className="text-sm text-gray-500">{invoice.customer.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatPaymentDate(invoice)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{getCurrencySymbol(invoice.currency || invoice.pricing?.currency)}{invoice.total.toFixed(2)}</div>
                      <div className="text-sm text-gray-500">{invoice.currency || invoice.pricing?.currency}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        invoice.status === 'paid' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {invoice.status === 'paid' ? 'Paid' : 'Unpaid'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{renderInvoiceType(invoice)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-1">
                        <motion.button 
                          onClick={() => handleCheckStatus(invoice)} 
                          className="text-teal-600 hover:text-teal-900 p-2 rounded-lg hover:bg-teal-50 inline-flex disabled:opacity-50 disabled:cursor-not-allowed" 
                          disabled={checkingStatusId === invoice.id || (!invoice.paymentIntentId && !invoice.checkoutSessionId)}
                          title={!invoice.paymentIntentId && !invoice.checkoutSessionId ? "Aucune tentative de paiement enregistrée" : "Vérifier le statut du paiement"}
                        >
                          {checkingStatusId === invoice.id ? <motion.div animate={{rotate:360}} transition={{duration:1,repeat:Infinity,ease:'linear'}}><FiLoader className="w-4 h-4"/></motion.div> : <FiRefreshCw className="w-4 h-4" />}
                        </motion.button>
                        <motion.button 
                          onClick={() => handleSendEmail(invoice)} 
                          className="text-indigo-600 hover:text-indigo-900 p-2 rounded-lg hover:bg-indigo-50 inline-flex disabled:opacity-50" 
                          disabled={sendingEmailId === invoice.id}
                          title="Send Email"
                        >
                          {sendingEmailId === invoice.id ? <motion.div animate={{rotate:360}} transition={{duration:1,repeat:Infinity,ease:'linear'}}><FiLoader className="w-4 h-4"/></motion.div> : <FiMail className="w-4 h-4" />}
                        </motion.button>
                        <Link to={`/preview/${invoice.id}`} className="text-green-600 hover:text-green-900 p-2 rounded-lg hover:bg-green-50 inline-flex" title="Preview Invoice">
                          <SafeIcon icon={FiEye} className="w-4 h-4" />
                        </Link>
                        <Link to={getEditPath(invoice)} className="text-yellow-600 hover:text-yellow-900 p-2 rounded-lg hover:bg-yellow-50 inline-flex" title="Edit">
                          <SafeIcon icon={FiEdit} className="w-4 h-4" />
                        </Link>
                        <motion.button onClick={() => handleDownload(invoice)} className="text-blue-600 hover:text-blue-900 p-2 rounded-lg hover:bg-blue-50" title="Download PDF">
                          <SafeIcon icon={FiDownload} className="w-4 h-4" />
                        </motion.button>
                        <motion.button onClick={() => handleDelete(invoice.id)} className="text-red-600 hover:text-red-900 p-2 rounded-lg hover:bg-red-50" title="Delete">
                          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                        </motion.button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceList;